import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { casesApi, analyticsApi } from '../services/api';
import { Case } from '../types';
import Onboarding from '../components/Onboarding';
import { ONBOARDING_STEPS, SAMPLE_CASE_DATA } from '../content/onboardingContent';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { fromConvexCase } from '@/lib/convexCaseAdapter';

function EmptyPanel({ message }: { message: string }) {
  return (
    <div className="h-40 flex items-center justify-center text-center text-sm text-paper-400 px-4">
      {message}
    </div>
  );
}

function StatCard({
  label,
  value,
  subtext,
  highlight = false,
  to,
}: {
  label: string;
  value: React.ReactNode;
  subtext?: React.ReactNode;
  highlight?: boolean;
  to?: string;
}) {
  const body = (
    <>
      <div className="text-[11px] font-semibold text-paper-500 uppercase tracking-wider mb-3">
        {label}
      </div>
      <div className={`text-4xl font-bold ${highlight ? 'text-forest-600' : 'text-navy-900'}`}>
        {value}
      </div>
      {subtext && <div className="text-sm text-paper-500 mt-2">{subtext}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="block bg-white border border-paper-300 p-6 hover:border-forest-500 hover:shadow-sm transition-colors">
        {body}
      </Link>
    );
  }
  return <div className="bg-white border border-paper-300 p-6">{body}</div>;
}

function TrendChart({ data }: { data: number[] }) {
  // preserveAspectRatio="none" over a fixed 100x60 viewBox used to stretch X
  // and Y by whatever different factors the actual (much wider than tall)
  // container happened to need to fill it — reported live as the chart and
  // its axis labels looking stretched sideways. Measuring the real rendered
  // size and using it as the viewBox means 1 viewBox unit is always exactly
  // 1 real pixel in both directions, so nothing needs non-uniform scaling.
  const containerRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 600, height: 224 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) setBox({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (data.length === 1) {
    return (
      <div className="h-56 w-full flex flex-col items-center justify-center gap-2">
        <div className="text-3xl font-bold text-forest-600">{Math.round(data[0])}%</div>
        <p className="text-sm text-paper-400 text-center max-w-xs">
          Only one simulation so far. Run another to start seeing a trend line.
        </p>
      </div>
    );
  }

  // Auto-scale the Y axis to the data range so small movements (e.g. 72 vs 78)
  // are visible instead of looking like a flat line against a fixed 0-100 scale.
  const min = Math.min(...data);
  const max = Math.max(...data);
  const pad = Math.max(2, (max - min) * 0.12);
  const yMin = Math.max(0, min - pad);
  const yMax = Math.min(100, max + pad);
  const yRange = yMax - yMin || 1;
  const { width, height } = box;
  const chartBottom = height - 16; // leave room for x-axis labels below the line
  const yFor = (v: number) => chartBottom - ((Math.min(Math.max(v, yMin), yMax) - yMin) / yRange) * chartBottom;
  const xFor = (i: number) => (data.length === 1 ? width / 2 : (i / (data.length - 1)) * width);
  const points = data.map((v, i) => [xFor(i), yFor(v)] as const);

  return (
    <div ref={containerRef} className="h-56 w-full relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full">
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2d5a3d" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#2d5a3d" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.33, 0.66, 1].map((t) => {
          const y = yMin + yRange * t;
          const yPos = chartBottom - t * chartBottom;
          return (
            <g key={t}>
              <line x1="0" y1={yPos} x2={width} y2={yPos} stroke="#ebe8e0" strokeWidth="1" />
              <text x="4" y={yPos - 4} fontSize="10" fill="#a8a29e">{Math.round(y)}%</text>
            </g>
          );
        })}
        <polygon
          points={`0,${chartBottom} ${points.map(([x, y]) => `${x},${y}`).join(' ')} ${width},${chartBottom}`}
          fill="url(#trendFill)"
        />
        <polyline
          points={points.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke="#2d5a3d"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="4" fill="#2d5a3d" />
        ))}
      </svg>
    </div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-base font-semibold text-navy-900">{title}</h3>
      {subtitle && <p className="text-sm text-paper-500 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function ArgumentResonance({ caseAnalytics }: { caseAnalytics: any }) {
  const items = useMemo<{ name: string; score: number }[]>(() => {
    if (caseAnalytics?.themeBreakdown?.length) {
      return caseAnalytics.themeBreakdown.map((t: any) => ({
        name: t.theme,
        // Resonance is a 0-100 scale, but model-generated resonance_matrix
        // scores occasionally come back negative (e.g. -7). A negative value
        // used directly as a CSS width is an invalid declaration, so the bar
        // div falls back to full width: a -7 looked like a full bar while a
        // legitimate +40 rendered at 40%. Clamp here so the bar length and
        // the printed number always agree.
        score: Math.max(0, Math.min(100, Math.round(t.avgScore || 0))),
      }));
    }
    return [];
  }, [caseAnalytics]);

  if (items.length === 0) {
    return <EmptyPanel message="Run a simulation to see argument resonance by theme." />;
  }

  return (
    <div className="space-y-4">
      {items.map((item) => (
        <div key={item.name}>
          <div className="flex items-center justify-between text-sm mb-1.5">
            <span className="text-navy-800">{item.name}</span>
            <span className="font-semibold text-navy-900">{item.score}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-paper-200 overflow-hidden">
              <div className="h-full bg-forest-500" style={{ width: `${item.score}%` }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArgumentStrengthHeatmap({ caseAnalytics }: { caseAnalytics: any }) {
  const data = caseAnalytics?.archetypeHeatmap;

  if (!data || data.values.length === 0) {
    return <EmptyPanel message="Run a simulation to see argument strength by jury archetype." />;
  }

  const { themes, groups, values } = data;
  const valueMap = new Map(values.map((v: any) => [`${v.theme}|${v.group}`, v.score]));

  // Simple green heat scale: 0 = near-white, 100 = forest green.
  function cellStyle(score: number): React.CSSProperties {
    const t = Math.max(0, Math.min(100, score)) / 100;
    // Interpolate from #f0fdf4 (light) to #14532d (dark forest).
    const r = Math.round(240 + t * (20 - 240));
    const g = Math.round(253 + t * (83 - 253));
    const b = Math.round(244 + t * (45 - 244));
    return {
      backgroundColor: `rgb(${r}, ${g}, ${b})`,
      color: t > 0.55 ? '#ffffff' : '#1a202c',
    };
  }

  // Full-width row + fixed table layout: columns share the available width
  // evenly and header text wraps, so the heatmap never needs a horizontal
  // scrollbar regardless of how many archetype columns come back.
  return (
    <div>
      <table className="w-full text-xs table-fixed">
        <thead>
          <tr>
            <th className="text-left py-2 px-2 font-medium text-paper-500 align-bottom w-[16%]">Theme</th>
            {groups.map((g: string) => (
              <th key={g} className="text-center py-2 px-1 font-medium text-paper-500 align-bottom break-words">{g}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {themes.map((theme: string) => (
            <tr key={theme} className="border-t border-paper-200">
              <td className="py-2 px-2 font-medium text-navy-800 break-words">{theme}</td>
              {groups.map((g: string) => {
                const score = valueMap.get(`${theme}|${g}`);
                return (
                  <td key={g} className="py-2 px-2 text-center font-semibold">
                    {typeof score === 'number' ? (
                      <span className="inline-block w-full py-2 px-1 rounded-sm" style={cellStyle(score)}>
                        {Math.round(score)}
                      </span>
                    ) : (
                      <span className="text-paper-300">-</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DashboardPage() {
  useDocumentTitle('Dashboard');
  const { isDemoMode } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string>('all');
  const [userSummary, setUserSummary] = useState<any>(null);
  const [caseAnalytics, setCaseAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Real mode: reactive Convex queries replace the one-shot Express fetches
  // below. Demo mode keeps the effects that call casesApi/analyticsApi.
  const casesQuery = useQuery(api.cases.list, isDemoMode ? 'skip' : {});
  const userSummaryQuery = useQuery(api.analytics.getUserSummary, isDemoMode ? 'skip' : {});
  const caseAnalyticsQuery = useQuery(
    api.analytics.getCaseAnalytics,
    !isDemoMode && selectedCaseId !== 'all' ? { caseId: selectedCaseId as Id<'cases'> } : 'skip'
  );
  const navigate = useNavigate();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [tourKey, setTourKey] = useState(0);
  const [showTour, setShowTour] = useState(() => {
    const completed = localStorage.getItem('sentantia_onboarding_completed');
    return completed !== 'true' || query.get('tour') === 'restart';
  });

  useEffect(() => {
    if (query.get('tour') === 'restart') {
      localStorage.removeItem('sentantia_onboarding_completed');
      setShowTour(true);
      setTourKey((k) => k + 1);
      // Clean the query param so a refresh doesn't restart the tour
      const clean = new URLSearchParams(location.search);
      clean.delete('tour');
      navigate(location.pathname + (clean.toString() ? `?${clean.toString()}` : ''), { replace: true });
    }
  }, [query, location, navigate]);

  useEffect(() => {
    if (!isDemoMode) return;
    async function loadData() {
      try {
        const casesData = await casesApi.getAll();
        setCases(casesData.cases);
        const summary = await analyticsApi.getUserSummary();
        setUserSummary(summary);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setLoadError('Analytics couldn’t be loaded. Refresh to try again. If this keeps happening, contact support.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [isDemoMode]);

  useEffect(() => {
    if (!isDemoMode) return;
    if (selectedCaseId === 'all') {
      setCaseAnalytics(null);
      return;
    }
    async function loadCaseAnalytics() {
      try {
        const data = await analyticsApi.getCaseAnalytics(selectedCaseId);
        setCaseAnalytics(data);
      } catch (err) {
        console.error('Failed to load case analytics:', err);
        setCaseAnalytics(null);
      }
    }
    loadCaseAnalytics();
  }, [selectedCaseId, isDemoMode]);

  // Real mode: cases/summary/case-analytics come from the reactive Convex
  // queries above.
  useEffect(() => {
    if (isDemoMode) return;
    if (casesQuery !== undefined) setCases(casesQuery.map(fromConvexCase));
    if (userSummaryQuery !== undefined) setUserSummary(userSummaryQuery);
    setLoading(casesQuery === undefined || userSummaryQuery === undefined);
  }, [isDemoMode, casesQuery, userSummaryQuery]);

  useEffect(() => {
    if (isDemoMode) return;
    if (selectedCaseId === 'all') {
      setCaseAnalytics(null);
      return;
    }
    setCaseAnalytics(caseAnalyticsQuery ?? null);
  }, [isDemoMode, selectedCaseId, caseAnalyticsQuery]);

  const selectedCase = cases.find((c) => c.id === selectedCaseId);
  const isAllCases = selectedCaseId === 'all';

  const metrics = useMemo(() => {
    if (isAllCases && userSummary) {
      return {
        activeCases: cases.filter((c) => c.status === 'active').length,
        archivedCases: cases.filter((c) => c.status === 'closed').length,
        totalSimulations: userSummary.totalSimulations || 0,
        avgResonance: userSummary.overallAvgResonance || 0,
        bestCaseScore: userSummary.bestCase?.avgResonance || 0,
        bestCaseName: userSummary.bestCase?.caseName || '-',
      };
    }
    if (caseAnalytics) {
      return {
        activeCases: selectedCase?.status === 'active' ? 1 : 0,
        archivedCases: selectedCase?.status === 'closed' ? 1 : 0,
        totalSimulations: caseAnalytics.totalSimulations || 0,
        avgResonance: caseAnalytics.averageResonance || 0,
        bestCaseScore: caseAnalytics.bestResonance || 0,
        bestCaseName: selectedCase?.name || '-',
      };
    }
    return null;
  }, [isAllCases, userSummary, caseAnalytics, cases, selectedCase]);

  const trendData = useMemo<number[]>(() => {
    if (caseAnalytics?.resonanceTrend?.length > 0) {
      return caseAnalytics.resonanceTrend.map((p: any) => p.resonance);
    }
    if (userSummary?.recentActivity?.length > 0 && isAllCases) {
      return userSummary.recentActivity.slice(-12).map((a: any) => a.resonance);
    }
    return [];
  }, [caseAnalytics, userSummary, isAllCases]);

  const caseComparison = useMemo<{ name: string; score: number }[]>(() => {
    if (!isAllCases && caseAnalytics?.totalSimulations > 0) {
      return [
        { name: 'Best simulation', score: caseAnalytics.bestResonance || 0 },
        { name: 'Worst simulation', score: caseAnalytics.worstResonance || 0 },
      ];
    }
    if (isAllCases && userSummary?.caseComparison?.length > 0) {
      return userSummary.caseComparison
        .slice(0, 5)
        .map((c: any) => ({ name: c.caseName, score: c.avgResonance || 0 }));
    }
    return [];
  }, [isAllCases, caseAnalytics, userSummary]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-paper-200 rounded w-1/3"></div>
          <div className="h-32 bg-paper-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold text-navy-900">Dashboard</h1>
          <p className="text-paper-500 mt-1">
            {isAllCases ? 'Portfolio overview · All cases' : `Case overview · ${selectedCase?.name}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <select
              value={selectedCaseId}
              onChange={(e) => {
                if (e.target.value === '__new__') {
                  navigate('/cases?new=1');
                  return;
                }
                setSelectedCaseId(e.target.value);
              }}
              className="appearance-none bg-white border border-paper-300 px-4 py-2.5 pr-10 text-sm text-navy-900 focus:outline-none focus:border-forest-500 min-w-[220px]"
            >
              <option value="all">All Cases ({cases.length})</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
              <option value="__new__">+ Add new case</option>
            </select>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-paper-400 pointer-events-none" aria-hidden="true">▼</span>
          </div>
          <button
            onClick={() => navigate('/simulations/new')}
            className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-navy-900 hover:bg-navy-800 transition-colors"
          >
            Run Simulation
          </button>
        </div>
      </div>

      {/* Load error */}
      {loadError && (
        <div className="bg-white border border-red-300 p-4">
          <p className="text-sm text-red-700">{loadError}</p>
        </div>
      )}

      {/* Stats Row */}
      {metrics ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label="Active Cases"
            value={metrics.activeCases}
            subtext={`${metrics.archivedCases} archived`}
            to="/cases"
          />
          <StatCard
            label="Total Simulations"
            value={metrics.totalSimulations}
            subtext={
              <span className="text-forest-600">Across portfolio</span>
            }
            to={isAllCases ? '/analytics' : `/cases/${selectedCaseId}`}
          />
          <StatCard
            label="Avg. Resonance"
            value={
              <span>
                {metrics.avgResonance}
                <span className="text-2xl">%</span>
              </span>
            }
            subtext={
              <span className="text-forest-600">{isAllCases ? 'All cases' : 'This case'}</span>
            }
            to="/analytics"
          />
          <StatCard
            label="Best Case Score"
            value={
              <span>
                {metrics.bestCaseScore}
                <span className="text-2xl">%</span>
              </span>
            }
            subtext={metrics.bestCaseName}
            highlight
            to={isAllCases ? (userSummary?.bestCase?.caseId ? `/cases/${userSummary.bestCase.caseId}` : undefined) : `/cases/${selectedCaseId}`}
          />
        </div>
      ) : (
        <div className="bg-white border border-paper-300 p-8 text-center">
          <p className="text-paper-500">No simulation data available yet.</p>
        </div>
      )}

      {/* Charts Row: three equal tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Resonance Trends */}
        <div className="bg-white border border-paper-300 p-6">
          <SectionTitle title="Resonance Trends" subtitle={isAllCases ? 'All active cases' : selectedCase?.name} />
          {trendData.length === 0 ? (
            <EmptyPanel message="Run a simulation to start tracking resonance over time." />
          ) : (
            <TrendChart data={trendData} />
          )}
        </div>

        {/* Argument Resonance */}
        <div className="bg-white border border-paper-300 p-6">
          <SectionTitle title="Argument Resonance" subtitle={selectedCase?.name || 'Portfolio'} />
          <ArgumentResonance caseAnalytics={isAllCases ? userSummary : caseAnalytics} />
        </div>

        {/* Case Comparison */}
        <div className="bg-white border border-paper-300 p-6">
          <SectionTitle title="Case Comparison" subtitle={isAllCases ? 'Best vs. lowest scoring cases' : 'Best vs. worst simulation'} />
          {caseComparison.length === 0 ? (
            <EmptyPanel
              message="Run at least one simulation to compare results."
            />
          ) : (
            <div className="space-y-5 pt-2">
              {caseComparison.map((item) => (
                <div key={item.name}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-navy-800">{item.name}</span>
                    <span className="font-semibold text-navy-900">{item.score}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-paper-200 overflow-hidden">
                      <div
                        className={`h-full ${item.score >= 80 ? 'bg-forest-500' : 'bg-navy-700'}`}
                        style={{ width: `${Math.max(0, Math.min(item.score, 100))}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Argument Strength Heatmap: full-width row so archetype columns fit
          without horizontal scrolling. */}
      <div className="bg-white border border-paper-300 p-6">
        <SectionTitle title="Argument Strength Heatmap" subtitle={`Score by jury archetype · ${selectedCase?.name || 'Portfolio'}`} />
        <ArgumentStrengthHeatmap caseAnalytics={isAllCases ? userSummary : caseAnalytics} />
      </div>

      {/* Empty state prompt */}
      {cases.length === 0 && (
        <div className="bg-white border border-paper-300 p-10 text-center">
          <h3 className="text-lg font-semibold text-navy-900 mb-2">No cases yet</h3>
          <p className="text-paper-500 mb-6 max-w-md mx-auto">
            Create your first case to start running simulations and building your dashboard.
          </p>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-navy-900 hover:bg-navy-800 transition-colors"
          >
            Go to Cases
          </Link>
        </div>
      )}

      {showTour && (
        <Onboarding
          key={tourKey}
          onboardingCompleted={false}
          onOnboardingComplete={() => {
            localStorage.setItem('sentantia_onboarding_completed', 'true');
            setShowTour(false);
          }}
          onOnboardingRestart={() => setTourKey((k) => k + 1)}
          sampleCase={SAMPLE_CASE_DATA}
          steps={ONBOARDING_STEPS}
        />
      )}
    </div>
  );
}
